import { ConfigurableColumnDef } from '@experiences/components/table/types';

export interface ConfigurableColumn {
  id: string;
  label: string;
}

export const isHideable = (column: ConfigurableColumnDef): boolean =>
  column.meta?.hideable !== false;

/**
 * Whether the column appears in the configure-columns popover. Excludes
 * non-hideable system columns and always-visible (locked) columns.
 */
export const isConfigurable = (column: ConfigurableColumnDef): boolean =>
  isHideable(column) && column.meta?.alwaysVisible !== true;

export const getEffectiveColumnOrder = (
  storedOrder: string[],
  availableColumnIds: string[],
): string[] => {
  const availableSet = new Set(availableColumnIds);
  const validStored = storedOrder.filter((id) => availableSet.has(id));
  const storedSet = new Set(validStored);
  // Insert columns missing from the stored order right after their nearest
  // default-order predecessor, so newly shipped columns surface in their
  // designed position instead of being tacked on the end.
  const result = [...validStored];
  let insertAfter = -1;
  for (const id of availableColumnIds) {
    if (storedSet.has(id)) {
      insertAfter = result.indexOf(id);
    } else {
      insertAfter += 1;
      result.splice(insertAfter, 0, id);
    }
  }
  return result;
};

export const getConfigurableColumns = (
  columns: ConfigurableColumnDef[],
): ConfigurableColumn[] =>
  columns
    .filter((column) => isConfigurable(column) && column.id)
    .map((column) => ({
      id: column.id!,
      label: column.meta?.label ?? column.id!,
    }));
